import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as client from "../../../client";
import {
  createEndorsement,
  deleteEndorsement,
  estimateEndorsementPremium,
  getEndorsementSovDiff,
  listEndorsements,
  regenerateEndorsement,
  suggestEndorsementPremium,
  updateEndorsementPremium,
  updateEndorsementStatus,
  uploadEndorsementDocuments,
} from "../../../api/routes";
import { endorsementKeys, reportKeys } from "../queryKeys";

export const useEndorsements = (reportId: string) => {
  return useQuery({
    queryKey: endorsementKeys.forReport(reportId),
    queryFn: () => listEndorsements(reportId),
    refetchOnWindowFocus: false,
    // Poll while a generation run is in flight so the tab picks up the
    // finished schedule (and its refreshed premium) on its own.
    refetchInterval: (query) =>
      query.state.data?.endorsements.some((e) => e.status === "generating")
        ? 10_000
        : false,
  });
};

export const useEndorsementSovDiff = (endorsementId: string) => {
  return useQuery({
    queryKey: endorsementKeys.sovDiff(endorsementId),
    queryFn: () => getEndorsementSovDiff(endorsementId),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// Creation and document upload also touch the report's attachments, so both
// invalidate the report detail alongside the ledger.
const useInvalidateEndorsements = (reportId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: endorsementKeys.forReport(reportId),
    });
    queryClient.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
  };
};

export const useCreateEndorsement = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["create-endorsement", reportId],
    mutationFn: (input: {
      policyApplicationId: string;
      effectiveDate: string;
      description: string;
      premiumBasis: client.EndorsementPremiumBasis;
      tivTarget: number | null;
      customTermPremiumDelta: number | null;
      files: File[];
    }) => createEndorsement({ reportId, ...input }),
    onSuccess: invalidate,
  });
};

export const useUploadEndorsementDocuments = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["upload-endorsement-documents", reportId],
    mutationFn: (input: { endorsementId: string; files: File[] }) =>
      uploadEndorsementDocuments(input.endorsementId, input.files),
    onSuccess: invalidate,
  });
};

// Up-front account-rate estimate while drafting: rates from the report's
// current TIV toward the entered target. Enabled only once every input is set.
export const useEstimateEndorsementPremium = (input: {
  reportId: string;
  policyApplicationId: string | null;
  effectiveDate: string | null;
  tivTarget: number | null;
  enabled: boolean;
}) => {
  const ready =
    input.enabled &&
    input.policyApplicationId !== null &&
    input.effectiveDate !== null &&
    input.tivTarget !== null &&
    input.tivTarget > 0;
  return useQuery({
    queryKey: endorsementKeys.estimate(
      input.reportId,
      input.policyApplicationId ?? "",
      input.effectiveDate ?? "",
      input.tivTarget ?? 0
    ),
    queryFn: () =>
      estimateEndorsementPremium({
        reportId: input.reportId,
        policyApplicationId: input.policyApplicationId ?? "",
        effectiveDate: input.effectiveDate ?? "",
        tivTarget: input.tivTarget ?? 0,
      }),
    enabled: ready,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

// The server-computed premium change for a saved endorsement (account-rate
// delta, or just the proration factor for other bases). A pure read: fetched
// while the premium editor is open for that endorsement.
export const useSuggestEndorsementPremium = (endorsementId: string | null) => {
  return useQuery({
    queryKey: endorsementKeys.suggest(endorsementId ?? ""),
    queryFn: () => suggestEndorsementPremium(endorsementId ?? ""),
    enabled: endorsementId !== null,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useUpdateEndorsementPremium = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["update-endorsement-premium", reportId],
    mutationFn: (input: {
      endorsementId: string;
      premiumChange: client.EndorsementPremiumChange;
    }) => updateEndorsementPremium(input.endorsementId, input.premiumChange),
    onSuccess: invalidate,
  });
};

export const useUpdateEndorsementStatus = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["update-endorsement-status", reportId],
    mutationFn: (input: {
      endorsementId: string;
      status: client.EndorsementStatus;
    }) => updateEndorsementStatus(input.endorsementId, input.status),
    onSuccess: invalidate,
  });
};

export const useDeleteEndorsement = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["delete-endorsement", reportId],
    mutationFn: (endorsementId: string) => deleteEndorsement(endorsementId),
    onSuccess: invalidate,
  });
};

export const useRegenerateEndorsement = (reportId: string) => {
  const invalidate = useInvalidateEndorsements(reportId);
  return useMutation({
    mutationKey: ["regenerate-endorsement", reportId],
    mutationFn: (endorsementId: string) => regenerateEndorsement(endorsementId),
    onSuccess: invalidate,
  });
};
